import React, { useState, useEffect } from 'react';
import { Box, Button, DialogActions, DialogContent, DialogTitle, MenuItem, TextField, Checkbox, FormControlLabel } from '@mui/material';
// toast import removed
const roleOptions = ['admin', 'receptionist', 'event-coordinator', 'restaurant-manager', 'frontdesk-manager'];

export default function UserForm({ initialData = {}, onSave, onCancel, currentUserId, currentUserRole }) {
  const [form, setForm] = useState(initialData);
  const [changePassword, setChangePassword] = useState(false);
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [oldPasswordError, setOldPasswordError] = useState('');
  const [confirmPasswordError, setConfirmPasswordError] = useState('');
  const [fieldError, setFieldError] = useState({ username: '', password: '', email: '', phone: '' });
  const [showPassword, setShowPassword] = useState(false);

  // Debugging log for role and ids
  console.log('👤 Role Debug → CurrentUserRole:', currentUserRole, 'FormUserId:', form._id, 'CurrentUserId:', currentUserId);

  useEffect(() => {
    setForm({
      ...initialData,
      role: initialData.role || 'receptionist', // fallback to admin or desired default
    });
  }, [initialData]);

  const handleChange = (key, value) => {
    setForm(prev => ({ ...prev, [key]: value }));
  };

  const togglePasswordVisibility = () => setShowPassword(prev => !prev);

  const handleSubmit = async () => {
    // Reset field errors for email and phone before validation
    setFieldError({ username: '', password: '', email: '', phone: '' });
    if (!form.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      setFieldError(prev => ({ ...prev, email: 'Please enter a valid email address' }));
      return;
    }
    if (!form.phone || !/^\d{8,15}$/.test(form.phone)) {
      setFieldError(prev => ({ ...prev, phone: 'Please enter a valid phone number (8–15 digits)' }));
      return;
    }
    const submitData = { ...form };
    if (changePassword) {
      submitData.oldPassword = oldPassword;
      submitData.newPassword = newPassword;
      submitData.confirmPassword = confirmPassword;
    }
    if (!form._id && form.password !== confirmPassword) {
      setConfirmPasswordError('Password and confirmation do not match');
      return;
    }
    // Prevent saving if new password and confirmation do not match
    if (changePassword && newPassword !== confirmPassword) {
      setConfirmPasswordError('New password and confirmation do not match');
      return;
    }
    if (!form._id) {
      submitData.password = form.password;
      // Add property info for new users from localStorage
      const storedProperty = JSON.parse(localStorage.getItem('property'));
      console.log('📦 Loaded property from localStorage:', storedProperty);
      if (storedProperty && storedProperty._id) {
        submitData.properties = [{
          propertyId: storedProperty._id,
          role: form.role || 'receptionist'
        }];
      }
    }
    if (form.properties?.[0]) {
      form.properties[0].role = form.role;
    }
    console.log('📝 Submitting user data:', submitData);
    try {
      await onSave(submitData);
      // toast.success removed
    } catch (err) {
      console.log('❗ ERROR CAUGHT IN SUBMIT:', err);
      if (err?.error === 'Old password is incorrect') {
        setFieldError({ username: '', password: 'Old password is incorrect' });
      } else if (err?.error === 'Username already exists') {
        setFieldError({ ...fieldError, username: 'Username already exists' });
      } else {
        console.error('❌ Unhandled error during user save:', err?.error || err);
      }
    }
  };

  // Debugging log for role value
  return (
    <>
      <DialogTitle>{form._id ? 'Edit User' : 'Create User'}</DialogTitle>
      <DialogContent>
        <Box display="flex" flexDirection="column" gap={2} mt={1}>
          <TextField
            label="Username"
            value={form.username || ''}
            onChange={e => {
              handleChange('username', e.target.value);
              setFieldError({ ...fieldError, username: '' });
            }}
            fullWidth
            error={!!fieldError.username}
            helperText={fieldError.username}
          />
          <TextField
            label="Email"
            value={form.email || ''}
            onChange={e => {
              handleChange('email', e.target.value);
              setFieldError(prev => ({ ...prev, email: '' }));
            }}
            fullWidth
            error={!!fieldError.email}
            helperText={fieldError.email}
          />
          <TextField
            label="Phone"
            value={form.phone || ''}
            onChange={e => {
              handleChange('phone', e.target.value);
              setFieldError(prev => ({ ...prev, phone: '' }));
            }}
            fullWidth
            error={!!fieldError.phone}
            helperText={fieldError.phone}
          />
          
          {!form._id && (
            <>
              <TextField
                variant="outlined"
                label="Password"
                type={showPassword ? 'text' : 'password'}
                value={form.password || ''}
                onChange={e => handleChange('password', e.target.value)}
                fullWidth
                InputProps={{
                  endAdornment: (
                    <Button onClick={togglePasswordVisibility}>
                      {showPassword ? 'Hide' : 'Show'}
                    </Button>
                  )
                }}
              />
              <TextField
                variant="outlined"
                label="Confirm Password"
                type={showPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={e => {
                  setConfirmPassword(e.target.value);
                  setConfirmPasswordError('');
                }}
                error={!!confirmPasswordError}
                helperText={confirmPasswordError}
                fullWidth
                InputProps={{
                  endAdornment: (
                    <Button onClick={togglePasswordVisibility}>
                      {showPassword ? 'Hide' : 'Show'}
                    </Button>
                  )
                }}
              />
            </>
          )}
          {currentUserRole === 'admin' && form._id !== currentUserId ? (
            <TextField
              select
              label="Role"
              value={form.role || ''}
              onChange={e => handleChange('role', e.target.value)}
              fullWidth
            >
              {roleOptions.map(role => (
                <MenuItem key={role} value={role}>{role}</MenuItem>
              ))}
            </TextField>
          ) : (
            <TextField
              label="Role"
              value={form.role || ''}
              fullWidth
              InputProps={{ readOnly: true }}
            />
          )}
          {form._id && (
            <FormControlLabel
              control={<Checkbox checked={changePassword} onChange={e => setChangePassword(e.target.checked)} />}
              label="Change Password"
            />
          )}
          {changePassword && (
            <>
              <TextField
              variant="outlined"
              label="Old Password"
              type={showPassword ? 'text' : 'password'}
              value={oldPassword}
              onChange={e => {
                setOldPassword(e.target.value);
                setOldPasswordError('');
              }}
              error={!!fieldError.password || !!oldPasswordError}
              helperText={fieldError.password || oldPasswordError}
              fullWidth
              InputProps={{
                endAdornment: (
                  <Button onClick={togglePasswordVisibility}>
                    {showPassword ? 'Hide' : 'Show'}
                  </Button>
                )
              }}
            
            />
              <TextField
                variant="outlined"
                label="New Password"
                type={showPassword ? 'text' : 'password'}
                value={newPassword}
                onChange={e => setNewPassword(e.target.value)}
                fullWidth
                InputProps={{
                  endAdornment: (
                    <Button onClick={togglePasswordVisibility}>
                      {showPassword ? 'Hide' : 'Show'}
                    </Button>
                  )
                }}
              />
              <TextField
                variant="outlined"
                label="Confirm New Password"
                type={showPassword ? 'text' : 'password'}
                value={confirmPassword}
                error={!!confirmPasswordError}
                helperText={confirmPasswordError}
                onChange={e => {
                  setConfirmPassword(e.target.value);
                  setConfirmPasswordError('');
                }}
                fullWidth
                InputProps={{
                  endAdornment: (
                    <Button onClick={togglePasswordVisibility}>
                      {showPassword ? 'Hide' : 'Show'}
                    </Button>
                  )
                }}
              />
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleSubmit} variant="contained">Save</Button>
        <Button onClick={onCancel}>Cancel</Button>
      </DialogActions>
      {/* ToastContainer removed */}
    </>
  );
}